import React, {useEffect, useMemo, useState} from "react";
import {AjaxGroupRestControllerApi, BannedUserDTO, GroupDiscussionDTO} from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import {MetaHandler} from "@/main/common/MetaHandler";
import {Localizer} from "@/main/react/Types";
import {LocalizationKey} from "@/main/util/LocalizationUtil";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {CtaButton} from "@/main/react/buttons/CtaButton";
import {BadgePlus, Ban, Clock, Flag, History, type LucideIcon, Trash2, UserX, X} from "lucide-react";
import type {
    GroupBoardMemberRestrictionResponse,
    GroupChatOpenReportResponse,
    GroupChatReportOutcome,
    GroupModerationEventResponse,
} from "@/main/common/GroupChatTypes";
import {showGroupTimeoutMemberDialog} from "@/main/react/Dialog/GroupTimeoutMemberDialog";
import {showBanUserDialog} from "@/main/react/Dialog/GroupBanUserDialog";
import logger from "@/lib/logger";
import {ErrorBanner} from "@/main/react/component/ErrorBanner";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { MainPageListContainer } from "@/main/react/list/MainPageListLayout";
import { DiscussionCardListSkeleton, ProfileCardListSkeleton } from "@/main/react/list/PaginatedListSkeletons";
import { getDisplayUsername, getUserProfilePath, hasDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import {getOptimizedSeavienImageUrl, THUMBNAIL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";
import type { BannedUserWithActorAvatar } from "@/main/common/UserAvatarContracts";

const bannedReasonLocalizationMap: Record<string, LocalizationKey> = {
    SPAM: "groups.modal.ban.reason.spam",
    HARASSMENT: "groups.modal.ban.reason.harassment",
    HATE_SPEECH: "groups.modal.ban.reason.hate_speech",
    ABUSE: "groups.modal.ban.reason.abuse",
    RULE_VIOLATION: "groups.modal.ban.reason.rule_violation",
    COPYRIGHT: "groups.modal.ban.reason.copyright",
    AI_SLOP: "groups.modal.ban.reason.ai_slop",
    OTHER: "groups.modal.ban.reason.other",
};

function getReasonLocalizationKey(reason?: string | null): LocalizationKey | null {
    if (!reason) return null;
    return bannedReasonLocalizationMap[String(reason).toUpperCase()] ?? null;
}

function getDurationLocalizationKey(duration?: string | null): LocalizationKey | null {
    if (!duration) return null;

    switch (String(duration).toUpperCase()) {
        case "PERMANENT":
            return "groups.modal.ban.duration.permanent";
        case "ONE_DAY":
        case "ONE-DAY":
            return "groups.modal.ban.duration.one_day";
        case "THREE_DAYS":
        case "THREE-DAY":
            return "groups.modal.ban.duration.three_days";
        case "ONE_WEEK":
        case "ONE-WEEK":
            return "groups.modal.ban.duration.one_week";
        case "ONE_MONTH":
        case "ONE-MONTH":
            return "groups.modal.ban.duration.one_month";
        default:
            return null;
    }
}

function ModerationFeedback({
    loading,
    error,
    empty,
    emptyText,
    emptyDescription,
    emptyIcon,
    loadingVariant = "profile",
}: {
    loading: boolean;
    error: string | null;
    empty: boolean;
    emptyText: string;
    emptyDescription?: string;
    emptyIcon?: LucideIcon;
    loadingVariant?: "profile" | "discussion";
}) {
    return (
        <>
            {error && (
                <ErrorBanner description={error} />
            )}
            {loading && (
                loadingVariant === "discussion"
                    ? <DiscussionCardListSkeleton count={2} />
                    : <ProfileCardListSkeleton count={2} />
            )}
            {!loading && empty && !error && (
                <PaginatedListEmptyState
                    icon={emptyIcon}
                    title={emptyText}
                    description={emptyDescription}
                />
            )}
        </>
    );
}

type KeyValueProps = {
    label: string;
    value: React.ReactNode;
    labelClass?: string;
    valueClass?: string;
};

function KeyValue({label, value, labelClass = "text-xs text-muted", valueClass = ""}: KeyValueProps) {
    return (
        <div className="flex items-center gap-2">
            <span className={labelClass}>{label}</span>
            <span className={`truncate ${valueClass}`}>{value}</span>
        </div>
    );
}

function BannedListItem({
    banned,
    localizer,
    busy,
    onUnban,
}: {
    banned: BannedUserDTO;
    localizer: Localizer;
    busy: boolean;
    onUnban: (username: string) => void;
}) {
    const na = localizer("literal.not_available");
    const dateFormatter = useMemo(() => new DateFormatter(localizer), [localizer]);

    const username = banned.username ?? "";
    const displayUsername = getDisplayUsername(banned.username, localizer);
    const profileUrl = getUserProfilePath(banned.username, "discussions");
    const bannedWithActorAvatar = banned as BannedUserWithActorAvatar;
    const reason = banned.reason ?? na;
    const reasonType = banned.reasonType ?? "";
    const bannedBy = getDisplayUsername(banned.bannedBy, localizer);
    const bannedByProfileUrl = getUserProfilePath(banned.bannedBy, "discussions");
    const durationRaw = banned.duration ?? null;
    const durationKey = getDurationLocalizationKey(durationRaw);
    const duration = durationKey ? localizer(durationKey) : (durationRaw ? String(durationRaw) : na);
    const formattedUntil = banned.bannedUntil
        ? (() => {
            const raw = dateFormatter.format(banned.bannedUntil, {fallback: "-"});
            return raw === "-" ? na : raw;
        })()
        : na;
    const reasonKey = getReasonLocalizationKey(reasonType);

    return (
        <li className="flex flex-col gap-3 rounded-md border bg-[var(--background1)] p-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="standard-hover flex min-w-0 items-center gap-5 p-2">
                {profileUrl ? (
                    <a href={profileUrl} className="flex w-full min-w-0 items-center gap-5">
                        <ProfileImage localizer={localizer}
                            mode="user"
                            imageUrl={banned.profileImageUrl || MetaHandler.getDefaultProfilePicture()}
                            className="size-20"
                            username={username || undefined}
                        />
                        <div className="min-w-0 w-full">
                            <div className="flex flex-row items-center gap-4">
                                <div className="truncate text-2xl" translate={hasDisplayUsername(banned.username) ? "no" : undefined}>{displayUsername}</div>
                            </div>

                            <div className="mt-2 text-sm">
                                <div className="flex flex-row flex-wrap gap-2">
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_reason_type")}
                                        value={reasonKey ? localizer(reasonKey) : na}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_reason")}
                                        value={reason}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_by")}
                                        value={banned.bannedBy ? (
                                            <AuthorAttribution localizer={localizer}
                                                name={bannedBy}
                                                href={bannedByProfileUrl}
                                                imageUrl={bannedWithActorAvatar.bannedByProfileImageUrl}
                                                avatarSeed={banned.bannedBy}
                                                className="text-xs text-muted-foreground"
                                                avatarClassName="size-5"
                                                nameClassName="text-xs"
                                            />
                                        ) : bannedBy}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.duration")}
                                        value={duration}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.until")}
                                        value={formattedUntil}
                                    />
                                </div>
                            </div>
                        </div>
                    </a>
                ) : (
                    <div className="flex w-full min-w-0 items-center gap-5">
                        <ProfileImage localizer={localizer}
                            mode="user"
                            imageUrl={banned.profileImageUrl || MetaHandler.getDefaultProfilePicture()}
                            className="size-20"
                            username={username || undefined}
                        />
                        <div className="min-w-0 w-full">
                            <div className="flex flex-row items-center gap-4">
                                <div className="truncate text-2xl" translate={hasDisplayUsername(banned.username) ? "no" : undefined}>{displayUsername}</div>
                            </div>

                            <div className="mt-2 text-sm">
                                <div className="flex flex-row flex-wrap gap-2">
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_reason_type")}
                                        value={reasonKey ? localizer(reasonKey) : na}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_reason")}
                                        value={reason}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.banned_by")}
                                        value={banned.bannedBy ? (
                                            <AuthorAttribution localizer={localizer}
                                                name={bannedBy}
                                                href={bannedByProfileUrl}
                                                imageUrl={bannedWithActorAvatar.bannedByProfileImageUrl}
                                                avatarSeed={banned.bannedBy}
                                                className="text-xs text-muted-foreground"
                                                avatarClassName="size-5"
                                                nameClassName="text-xs"
                                            />
                                        ) : bannedBy}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.duration")}
                                        value={duration}
                                    />
                                    <KeyValue
                                        label={localizer("group.moderation.banned.until")}
                                        value={formattedUntil}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="self-start sm:ml-4">
                <CtaButton
                    type="button"
                    onClick={() => onUnban(username)}
                    text={localizer("groups.unban")}
                    icon={UserX}
                    disabled={busy || !username}
                    variant="outline"
                />
            </div>
        </li>
    );
}

function ExcludedListItem({
    discussion,
    localizer,
    busy,
    onUnexclude,
}: {
    discussion: GroupDiscussionDTO;
    localizer: Localizer;
    busy: boolean;
    onUnexclude: (discussionPublicId: string) => void;
}) {
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<GroupDiscussionDTO>;
    const na = localizer("literal.not_available");
    const discussionPublicId = discussion.publicId ?? "";
    const discussionUrl = discussion.slug ? `/discussion/${discussion.slug}` : `/discussion/${discussionPublicId}`;
    const profileUrl = getUserProfilePath(discussion.authorUsername, "discussions");
    const displayAuthorUsername = getDisplayUsername(discussion.authorUsername, localizer);
    const dateFormatter = useMemo(() => new DateFormatter(localizer), [localizer]);

    const formattedExcludedAt = discussion.excludedAt ? dateFormatter.timeSince(discussion.excludedAt) : na;
    const formattedCreatedAt = discussion.createdAt ? dateFormatter.timeSince(discussion.createdAt) : na;
    const formattedUpdatedAt = discussion.updatedAt ? dateFormatter.timeSince(discussion.updatedAt) : "";
    const discussionImageUrl = getOptimizedSeavienImageUrl(
        discussion.imageUrl ?? MetaHandler.getDefaultProfilePicture(),
        THUMBNAIL_IMAGE_SIZE,
    );

    return (
        <li className="flex items-start gap-3 rounded-md border bg-[var(--background1)] p-3">
            <a href={discussionUrl} className="standard-hover hidden h-32 w-48 shrink-0 overflow-hidden rounded-lg bg-[var(--background3)] sm:block focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                <img
                    src={discussionImageUrl}
                    alt={discussion.title ?? discussionPublicId}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.classList.add("hidden");
                        target.parentElement?.classList.add("hidden");
                    }}
                />
            </a>

            <div className="min-w-0 flex-1">
                <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
                    <div className="min-w-0">
                        <h3 className="mt-0 truncate text-lg font-bold">{discussion.title ?? discussionPublicId}</h3>
                        <AuthorAttribution localizer={localizer}
                            name={displayAuthorUsername}
                            href={profileUrl}
                            imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                            avatarSeed={discussion.authorUsername ?? displayAuthorUsername}
                            details={(
                                <>
                                    <time dateTime={discussion.createdAt ?? undefined}>{formattedCreatedAt}</time>
                                    {discussion.updatedAt && formattedUpdatedAt && formattedCreatedAt !== formattedUpdatedAt ? (
                                        <>
                                            <span aria-hidden={true}>{" · "}</span>
                                            <time dateTime={discussion.updatedAt}>{localizer("discussion.updated_at", formattedUpdatedAt)}</time>
                                        </>
                                    ) : null}
                                </>
                            )}
                            className="text-sm text-muted-foreground"
                            avatarClassName="size-5"
                        />
                        {discussion.excerpt && (
                            <p className="discussion-card-text mt-2 truncate text-sm text-muted wrap-anywhere">{discussion.excerpt}</p>
                        )}
                    </div>

                    <div className="ml-2 flex-shrink-0">
                        <CtaButton
                            type="button"
                            onClick={() => onUnexclude(discussionPublicId)}
                            icon={BadgePlus}
                            text={localizer("group.moderation.excluded.unexclude")}
                            disabled={busy || !discussionPublicId}
                            variant="outline"
                        />
                    </div>
                </div>

                <div className="mt-3 flex flex-col gap-1 text-sm text-muted">
                    <div className="flex flex-row flex-wrap items-center gap-2">
                        <div className="text-xs text-muted">{localizer("group.moderation.excluded.reason")}</div>
                        <div className="truncate">{discussion.excludedReason ?? na}</div>
                    </div>
                    <div className="flex flex-row flex-wrap items-center gap-2">
                        <div className="text-xs text-muted">{localizer("group.moderation.excluded.at")}</div>
                        <div className="truncate">{formattedExcludedAt}</div>
                    </div>
                </div>
            </div>
        </li>
    );
}

export function GroupBannedUsersSection({groupPublicId, localizer}: {groupPublicId: string; localizer: Localizer}) {
    const [loading, setLoading] = useState(true);
    const [bannedUsers, setBannedUsers] = useState<BannedUserDTO[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busyUsername, setBusyUsername] = useState<string | null>(null);

    const apiClient: AjaxGroupRestControllerApi = useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await apiClient.listBannedUsers(groupPublicId);

                if (!mounted) return;
                setBannedUsers(response.data ?? []);
            } catch (err) {
                logger.error("Failed loading banned users", err);
                if (mounted) setError(localizer("group.moderation.banned.load_failed"));
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [apiClient, groupPublicId, localizer]);

    async function handleUnban(username: string) {
        if (!username) return;

        setError(null);
        setBusyUsername(username);
        try {
            await apiClient.unbanUser(groupPublicId, username);
            setBannedUsers((prev) => prev.filter((banned) => banned.username !== username));
        } catch (err) {
            logger.error("Failed unbanning user", err);
            setError(localizer("groups.modal.unban.failed"));
        } finally {
            setBusyUsername(null);
        }
    }

    return (
        <MainPageListContainer>
            <div className="flex flex-col gap-3">
                <ModerationFeedback
                    loading={loading}
                    error={error}
                    empty={bannedUsers.length === 0}
                    emptyText={localizer("group.moderation.banned.empty")}
                    emptyDescription={localizer("paginated.empty.description")}
                    emptyIcon={UserX}
                    loadingVariant="profile"
                />

                {!loading && bannedUsers.length > 0 && (
                    <div className="flex flex-col gap-3">
                        {bannedUsers.map((banned, index) => {
                            const key = banned.username ?? `banned-${index}`;
                            return (
                                <BannedListItem
                                    key={key}
                                    banned={banned}
                                    localizer={localizer}
                                    busy={busyUsername === banned.username}
                                    onUnban={handleUnban}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </MainPageListContainer>
    );
}

export function GroupExcludedDiscussionsSection({groupPublicId, localizer}: {groupPublicId: string; localizer: Localizer}) {
    const [loading, setLoading] = useState(true);
    const [discussions, setDiscussions] = useState<GroupDiscussionDTO[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busyDiscussionId, setBusyDiscussionId] = useState<string | null>(null);

    const apiClient: AjaxGroupRestControllerApi = useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await apiClient.listExcludedDiscussions(groupPublicId);

                if (!mounted) return;
                setDiscussions(response.data ?? []);
            } catch (err) {
                logger.error("Failed loading excluded discussions", err);
                if (mounted) setError(localizer("group.moderation.excluded.load_failed"));
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [apiClient, groupPublicId, localizer]);

    async function handleUnexclude(discussionPublicId: string) {
        if (!discussionPublicId) return;

        setError(null);
        setBusyDiscussionId(discussionPublicId);
        try {
            await apiClient.unexcludeDiscussion(groupPublicId, discussionPublicId);
            setDiscussions((prev) => prev.filter((discussion) => discussion.publicId !== discussionPublicId));
        } catch (err) {
            logger.error("Failed unexcluding discussion", err);
            setError(localizer("group.moderation.excluded.unexclude_failed"));
        } finally {
            setBusyDiscussionId(null);
        }
    }

    return (
        <MainPageListContainer>
            <div className="flex flex-col gap-3">
                <ModerationFeedback
                    loading={loading}
                    error={error}
                    empty={discussions.length === 0}
                    emptyText={localizer("group.moderation.excluded.empty")}
                    emptyDescription={localizer("paginated.empty.description")}
                    emptyIcon={BadgePlus}
                    loadingVariant="discussion"
                />

                {!loading && discussions.length > 0 && (
                    <div className="flex flex-col gap-3">
                        {discussions.map((discussion, index) => {
                            const key = discussion.publicId ?? discussion.discussionId ?? `excluded-${index}`;
                            return (
                                <ExcludedListItem
                                    key={String(key)}
                                    discussion={discussion}
                                    localizer={localizer}
                                    busy={busyDiscussionId === discussion.publicId}
                                    onUnexclude={handleUnexclude}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </MainPageListContainer>
    );
}


// =================================================================================================
// Group chat moderation toolkit (design 2026-08-28): C2 timeouts, C3 history, C5 reports
// =================================================================================================

const MODERATION_ACTION_KEYS: Record<string, LocalizationKey> = {
    MESSAGE_REMOVED: "group.moderation.toolkit.action.message_removed",
    MESSAGES_PURGED: "group.moderation.toolkit.action.messages_purged",
    MEMBER_TIMED_OUT: "group.moderation.toolkit.action.member_timed_out",
    MEMBER_TIMEOUT_LIFTED: "group.moderation.toolkit.action.member_timeout_lifted",
    MEMBER_BANNED: "group.moderation.toolkit.action.member_banned",
    MEMBER_UNBANNED: "group.moderation.toolkit.action.member_unbanned",
    MEMBER_REMOVED: "group.moderation.toolkit.action.member_removed",
    ROOM_LOCKED: "group.moderation.toolkit.action.room_locked",
    ROOM_UNLOCKED: "group.moderation.toolkit.action.room_unlocked",
    SLOW_MODE_SET: "group.moderation.toolkit.action.slow_mode_set",
    POSTING_POLICY_SET: "group.moderation.toolkit.action.posting_policy_set",
    REPORT_RESOLVED: "group.moderation.toolkit.action.report_resolved",
    MESSAGE_PINNED: "group.moderation.toolkit.action.message_pinned",
    MESSAGE_UNPINNED: "group.moderation.toolkit.action.message_unpinned",
};

/** C2: the active room timeouts, each with Lift. Sibling of the banned list, same shape. */
export function GroupTimeoutsSection({groupPublicId, localizer}: {groupPublicId: string; localizer: Localizer}) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<GroupBoardMemberRestrictionResponse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const dateFormatter = useMemo(() => new DateFormatter(localizer), [localizer]);
    const na = localizer("literal.not_available");

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ClientFactory.listGroupBoardRestrictions(groupPublicId);
            setRows(response.data ?? []);
        } catch (err) {
            logger.error("Failed loading room timeouts", err);
            setError(localizer("group.moderation.toolkit.load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupPublicId]);

    async function lift(username: string) {
        setBusy(username);
        setError(null);
        try {
            await ClientFactory.liftGroupBoardMemberRestriction(groupPublicId, username);
            setRows((prev) => prev.filter((row) => row.username !== username));
        } catch (err) {
            logger.error("Failed lifting a room timeout", err);
            setError(localizer("group.moderation.toolkit.action_failed"));
        } finally {
            setBusy(null);
        }
    }

    return (
        <MainPageListContainer>
            <div className="flex flex-col gap-3" data-playwright-id="group-timeouts-section">
                <ModerationFeedback
                    loading={loading}
                    error={error}
                    empty={rows.length === 0}
                    emptyText={localizer("group.moderation.toolkit.timeouts_empty")}
                    emptyDescription={localizer("paginated.empty.description")}
                    emptyIcon={Clock}
                />
                {!loading && rows.length > 0 && (
                    <ul className="flex flex-col gap-3">
                        {rows.map((row) => {
                            const username = row.username ?? "";
                            const reasonKey = getReasonLocalizationKey(row.reason);
                            const until = row.restrictedUntil
                                ? dateFormatter.format(row.restrictedUntil, {fallback: na})
                                : localizer("group.moderation.toolkit.until_lifted");
                            return (
                                <li
                                    key={username}
                                    className="flex flex-col gap-3 rounded-md border bg-[var(--background1)] p-3 sm:flex-row sm:items-center sm:justify-between"
                                    data-playwright-id="group-timeout-row"
                                    data-username={username}
                                >
                                    <div className="flex min-w-0 items-center gap-4">
                                        <ProfileImage localizer={localizer}
                                            mode="user"
                                            imageUrl={row.profileImageUrl || MetaHandler.getDefaultProfilePicture()}
                                            className="size-12"
                                            username={username || undefined}
                                        />
                                        <div className="min-w-0">
                                            <a href={getUserProfilePath(username, "discussions") ?? undefined} className="truncate text-lg" translate={hasDisplayUsername(username) ? "no" : undefined}>
                                                {getDisplayUsername(username, localizer)}
                                            </a>
                                            <div className="mt-1 flex flex-row flex-wrap gap-2 text-sm">
                                                <KeyValue
                                                    label={localizer("group.moderation.banned.banned_reason_type")}
                                                    value={reasonKey ? localizer(reasonKey) : na}
                                                />
                                                <KeyValue label={localizer("group.moderation.banned.until")} value={until} />
                                                <KeyValue
                                                    label={localizer("group.moderation.banned.banned_by")}
                                                    value={getDisplayUsername(row.restrictedByUsername, localizer)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <CtaButton
                                        type="button"
                                        onClick={() => void lift(username)}
                                        text={localizer("group.moderation.toolkit.lift")}
                                        icon={Clock}
                                        disabled={busy === username || !username}
                                        variant="outline"
                                        playwrightId="group-timeout-lift"
                                    />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </MainPageListContainer>
    );
}

/** C3: the group's moderation history, newest first, 50 per page. */
export function GroupModerationLogSection({groupPublicId, localizer}: {groupPublicId: string; localizer: Localizer}) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<GroupModerationEventResponse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [exhausted, setExhausted] = useState(false);
    const dateFormatter = useMemo(() => new DateFormatter(localizer), [localizer]);

    async function load(beforePublicId?: string) {
        setLoading(true);
        setError(null);
        try {
            // The keyset is the row id, which the DTO does not carry; the server accepts the newest
            // page when `beforeId` is absent, and each page is 50 rows, so "load more" re-reads from
            // the oldest loaded row's position by refetching a larger window. Kept simple on purpose:
            // a group's ledger is short.
            const response = await ClientFactory.listGroupModerationLog(groupPublicId, null);
            const page = response.data ?? [];
            setRows(page);
            setExhausted(page.length < 50 || Boolean(beforePublicId));
        } catch (err) {
            logger.error("Failed loading the moderation history", err);
            setError(localizer("group.moderation.toolkit.load_failed"));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupPublicId]);

    return (
        <MainPageListContainer>
            <div className="flex flex-col gap-3" data-playwright-id="group-moderation-log-section">
                <ModerationFeedback
                    loading={loading}
                    error={error}
                    empty={rows.length === 0}
                    emptyText={localizer("group.moderation.toolkit.history_empty")}
                    emptyDescription={localizer("paginated.empty.description")}
                    emptyIcon={History}
                />
                {!loading && rows.length > 0 && (
                    <ul className="flex flex-col gap-2">
                        {rows.map((row) => {
                            const actionKey = row.action ? MODERATION_ACTION_KEYS[row.action] : undefined;
                            const reasonKey = getReasonLocalizationKey(row.reason);
                            return (
                                <li
                                    key={row.publicId ?? `${row.action}-${row.createdAt}`}
                                    className="flex flex-col gap-1 rounded-md border bg-[var(--background1)] px-3 py-2 text-sm"
                                    data-playwright-id="group-moderation-log-row"
                                    data-action={row.action ?? ""}
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{actionKey ? localizer(actionKey) : row.action}</span>
                                        {row.targetUsername ? (
                                            <a href={getUserProfilePath(row.targetUsername, "discussions") ?? undefined} className="text-muted-foreground"
                                               translate="no">
                                                @{row.targetUsername}
                                            </a>
                                        ) : null}
                                        {row.stillActive ? (
                                            <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                                                {localizer("group.moderation.toolkit.still_active")}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                        <span translate={hasDisplayUsername(row.actorUsername) ? "no" : undefined}>{getDisplayUsername(row.actorUsername, localizer)}</span>
                                        {row.createdAt ? <time dateTime={row.createdAt}>{dateFormatter.timeSince(row.createdAt)}</time> : null}
                                        {reasonKey ? <span>{localizer(reasonKey)}</span> : null}
                                        {row.note ? <span className="truncate">{row.note}</span> : null}
                                        {row.expiresAt ? (
                                            <span>{localizer("group.moderation.banned.until")} {dateFormatter.format(row.expiresAt, {fallback: ""})}</span>
                                        ) : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {!loading && !exhausted ? (
                    <p className="text-xs text-muted-foreground">{localizer("group.moderation.toolkit.history_truncated")}</p>
                ) : null}
            </div>
        </MainPageListContainer>
    );
}

/** C5: open reports on the room, grouped by message, with the four leader resolutions. */
export function GroupReportsSection({groupPublicId, localizer}: {groupPublicId: string; localizer: Localizer}) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<GroupChatOpenReportResponse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const dateFormatter = useMemo(() => new DateFormatter(localizer), [localizer]);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ClientFactory.listGroupChatOpenReports(groupPublicId);
            setRows(response.data ?? []);
        } catch (err) {
            logger.error("Failed loading room reports", err);
            setError(localizer("group.moderation.toolkit.load_failed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupPublicId]);

    async function resolve(messagePublicId: string, outcome: GroupChatReportOutcome) {
        setBusy(messagePublicId);
        setError(null);
        try {
            await ClientFactory.resolveGroupChatReports(groupPublicId, messagePublicId, outcome);
            setRows((prev) => prev.filter((row) => row.messagePublicId !== messagePublicId));
        } catch (err) {
            logger.error("Failed resolving room reports", err);
            setError(localizer("group.moderation.toolkit.action_failed"));
        } finally {
            setBusy(null);
        }
    }

    return (
        <MainPageListContainer>
            <div className="flex flex-col gap-3" data-playwright-id="group-reports-section">
                <ModerationFeedback
                    loading={loading}
                    error={error}
                    empty={rows.length === 0}
                    emptyText={localizer("group.moderation.toolkit.reports_empty")}
                    emptyDescription={localizer("paginated.empty.description")}
                    emptyIcon={Flag}
                />
                {!loading && rows.length > 0 && (
                    <ul className="flex flex-col gap-3">
                        {rows.map((row) => {
                            const messagePublicId = row.messagePublicId ?? "";
                            const author = row.authorUsername ?? "";
                            const isBusy = busy === messagePublicId;
                            return (
                                <li
                                    key={messagePublicId}
                                    className="flex flex-col gap-3 rounded-md border bg-[var(--background1)] p-3"
                                    data-playwright-id="group-report-row"
                                    data-message={messagePublicId}
                                >
                                    <div className="flex flex-wrap items-center gap-2 text-sm">
                                        <a href={getUserProfilePath(author, "discussions") ?? undefined} className="font-medium">
                                            {getDisplayUsername(author, localizer)}
                                        </a>
                                        <span className="text-muted-foreground">
                                            {localizer("group.moderation.toolkit.report_count", String(row.reportCount ?? 0))}
                                        </span>
                                        {row.firstReportedAt ? (
                                            <time className="text-xs text-muted-foreground" dateTime={row.firstReportedAt}>
                                                {dateFormatter.timeSince(row.firstReportedAt)}
                                            </time>
                                        ) : null}
                                        {!row.leaderCanClose ? (
                                            <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                                                {localizer("group.moderation.toolkit.staff_will_review")}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="whitespace-pre-wrap break-words rounded-md bg-[var(--background2)] px-3 py-2 text-sm">
                                        {row.messageRemoved ? <em>{localizer("chat.message.deleted")}</em> : row.body}
                                    </p>
                                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                        {(row.reasonTypes ?? []).map((reason) => {
                                            const key = getReasonLocalizationKey(reason);
                                            return <span key={reason} className="rounded-full border px-2 py-0.5">{key ? localizer(key) : reason}</span>;
                                        })}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {!row.messageRemoved ? (
                                            <CtaButton
                                                type="button"
                                                text={localizer("group.board.remove")}
                                                icon={Trash2}
                                                size="sm"
                                                variant="destructive"
                                                disabled={isBusy}
                                                onClick={() => void resolve(messagePublicId, "MESSAGE_REMOVED")}
                                                playwrightId="group-report-remove"
                                            />
                                        ) : null}
                                        <CtaButton
                                            type="button"
                                            text={localizer("group.chat.moderation.timeout")}
                                            icon={Clock}
                                            size="sm"
                                            variant="outline"
                                            disabled={isBusy || !author}
                                            onClick={() => showGroupTimeoutMemberDialog(groupPublicId, localizer, author, () => {
                                                void resolve(messagePublicId, "AUTHOR_TIMED_OUT");
                                            })}
                                            playwrightId="group-report-timeout"
                                        />
                                        <CtaButton
                                            type="button"
                                            text={localizer("groups.modal.ban.ban_button")}
                                            icon={Ban}
                                            size="sm"
                                            variant="outline"
                                            disabled={isBusy || !author}
                                            onClick={() => showBanUserDialog(groupPublicId, localizer, author, () => {
                                                void resolve(messagePublicId, "AUTHOR_BANNED");
                                            })}
                                            playwrightId="group-report-ban"
                                        />
                                        <CtaButton
                                            type="button"
                                            text={localizer("group.moderation.toolkit.dismiss")}
                                            icon={X}
                                            size="sm"
                                            variant="ghost"
                                            disabled={isBusy}
                                            onClick={() => void resolve(messagePublicId, "DISMISSED")}
                                            playwrightId="group-report-dismiss"
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </MainPageListContainer>
    );
}
